import { ResultOf } from '@graphql-typed-document-node/core';
import { graphql } from '@mp/graphql/generated';
import { useGraphQLQuery } from '@mp/hooks/useGraphQLRequest';

const ProfessionalScreeningsGql = graphql(`
  query GetProfessionalScreeningsForPhoneScreeningScheduled(
    $input: ProfessionalScreeningQueryInput
  ) {
    professionalScreenings(input: $input) {
      scheduled_time
      screener_account_id
      id
      screener_account {
        first_name
      }
    }
  }
`);

export const RecruitersGql = graphql(`
  query GetRecruitersForPhoneScreeningScheduled($id: Int!) {
    administrator(id: $id) {
      id
      account_id
      account {
        first_name
      }
    }
  }
`);

const CurrentUserGql = graphql(`
  query GetCurrentUserForPhoneScreeningScheduled {
    me {
      professional {
        id
        enable_rescreening_automation
        screening_recruiter_id
        phone_screen_date_and_time
      }
    }
  }
`);

type CurrentUserProps = ResultOf<typeof CurrentUserGql>;
type RecruiterProps = ResultOf<typeof RecruitersGql>;
type ProfessionalScreeningsProps = ResultOf<typeof ProfessionalScreeningsGql>;

export const useCurrentUserForPhoneScreeningScheduled = () => {
  const { data: currentUserData, isLoading } = useGraphQLQuery<CurrentUserProps>({
    operationName: 'GetCurrentUserForPhoneScreeningScheduled',
    query: CurrentUserGql,
    variables: {},
  });
  const currentUser = currentUserData?.me;

  return { currentUser, isLoading };
};

export const useRecruitersForPhoneScreeningScheduled = (recruiterId?: number | null) => {
  const enabled = !!recruiterId;

  const { data, isLoading, isInitialLoading } = useGraphQLQuery<RecruiterProps>({
    operationName: 'GetRecruitersForPhoneScreeningScheduled',
    queryKey: ['administrator', recruiterId],
    query: RecruitersGql,
    variables: { id: recruiterId },
    enabled: !!recruiterId,
  });

  const recruiter = data?.administrator;

  return { recruiter, isLoading: enabled ? isLoading : isInitialLoading };
};

export const useProfessionalScreeningsForPhoneScreeningScheduled = () => {
  const { currentUser } = useCurrentUserForPhoneScreeningScheduled();
  const enabled = !!currentUser?.professional?.id;
  const { data, isLoading, isInitialLoading } = useGraphQLQuery<ProfessionalScreeningsProps>({
    operationName: 'GetProfessionalScreeningsForPhoneScreeningScheduled',
    query: ProfessionalScreeningsGql,
    variables: {
      input: {
        search: {
          professional_id: currentUser?.professional?.id,
          reason: 'application',
          screening_type: 'phone',
          is_canceled: false,
          no_show: false,
        },
        orderBy: {
          scheduled_time: false,
        },
        perPage: 1,
      },
    },
    enabled,
  });
  const professionalScreening = data?.professionalScreenings?.[0];

  return { professionalScreening, isLoading: enabled ? isLoading : isInitialLoading };
};
